import { useNotification, useRouteQuery, useTranslator } from '@/hooks'
import { Card, Typography, Divider, IconButton } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { Button } from '@/@core/components/mui/Button'
import { formatMonthDisplay } from '@/utils/formatDate'
import { AttendanceEmployeeInformation } from './AttendanceEmployeeInformation'
import { AttendanceEmployeeTotal } from './AttendanceEmployeeTotal'
import { AttendanceEmployeeTable } from '../tables/AttendanceEmployeeTable'
import FormInput from '@/components/form/FormInput'
import { yupResolver } from '@hookform/resolvers/yup'
import { attendancePayrollSchema } from '@/@data/attendances/schema/attendance-payroll'
import { IHttpError, useUpdateAttendancePayrollByCompanyIdEmployeeMutation } from '@/@data'
import dayjs from 'dayjs'

export const AttendanceEmployeeEditPayroll = ({
  dataEmployee,
  setIsEditPayroll,
  onSuccess,
  dataCompany
}: {
  dataEmployee?: any
  setIsEditPayroll: (isEditPayroll: boolean) => void
  onSuccess: () => void
  dataCompany?: any
}) => {
  const { __ } = useTranslator()
  const n = useNotification()
  const [isToggle, setIsToggle] = useState(true)
  const { searchQuery } = useRouteQuery()
  const { id } = useParams()

  const monthParam = searchQuery.get('month')
  const { monthName, dateRange } = formatMonthDisplay(monthParam)

  console.log(dateRange)

  // default value
  useEffect(() => {
    methods.reset({
      net_pay: dataEmployee?.data?.data?.net_pay,
      gross_pay: dataEmployee?.data?.data?.gross_pay,
      daily_wage: dataEmployee?.data?.data?.daily_wage
    })
  }, [monthParam])

  const methods = useForm<any>({
    defaultValues: {
      month: '',
      net_pay: undefined,
      gross_pay: undefined,
      daily_wage: undefined
    },
    resolver: yupResolver(attendancePayrollSchema(__)),
    mode: 'onChange'
  })

  const { mutate: mutateUpdateAttendancePayroll, isPending: isUpdateAttendancePayrollPending } =
    useUpdateAttendancePayrollByCompanyIdEmployeeMutation()

  const onSubmit = async (data: any) => {
    const payload = {
      ...data,
      month: monthParam
    }

    mutateUpdateAttendancePayroll(
      {
        companyId: Number(searchQuery.get('companyId')),
        userId: Number(id),
        data: payload
      },
      {
        onSuccess: () => {
          n.success(__('Payroll updated successfully'))
          onSuccess()
          setIsEditPayroll(false)
        },
        onError: (error: IHttpError) => {
          n.error(error.message)
        }
      }
    )
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className='flex justify-between items-start gap-5'>
          <Card sx={{ borderRadius: 3, flex: 1 }}>
            <div className='p-4'>
              <div className='flex justify-between items-center pb-3'>
                <div className='flex items-center gap-3'>
                  <Button
                    variant='outlined'
                    size='small'
                    className='border-blue-500 text-blue-600 hover:border-blue-600'
                  >
                    <span className='text-[15px] font-medium max-w-[500px] whitespace-nowrap overflow-hidden text-ellipsis' title={dataEmployee?.data?.data?.user?.company?.name || '-'}>
                      {dataEmployee?.data?.data?.user?.company?.name || '-'}
                    </span>
                  </Button>
                  <Divider orientation='vertical' flexItem className='w-[2px] bg-[#2f2b3d1f]' />
                  <Typography variant='h6' className='text-[18px] font-medium'>
                    {__('Defaut Attendance/Payroll')}
                  </Typography>
                </div>
                <div className='flex items-center gap-2'>
                  <IconButton
                    size='small'
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#e0e0e0'
                      }
                    }}
                    onClick={() => {
                      setIsEditPayroll(false)
                    }}
                  >
                    <i className='tabler-x text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size='small'
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#e0e0e0'
                      }
                    }}
                    onClick={() => {
                      setIsToggle(!isToggle)
                    }}
                  >
                    <i className='tabler-viewport-wide text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                  </IconButton>
                </div>
              </div>
              <Divider />
              <div className='flex justify-between items-center py-3'>
                <div className='flex items-center gap-3'>
                  <div className='flex items-center gap-3'>
                    <Typography variant='h6' className='text-gray-800 font-medium text-[24px]'>
                      {monthName || '-'}
                    </Typography>
                  </div>
                </div>
                <div className='flex items-center gap-3'>
                  <Button
                    type='button'
                    variant='outlined'
                    size='small'
                    className='border-blue-500 text-blue-600 hover:border-blue-600'
                  >
                    <span className='text-[15px] font-medium'>
                      { dataCompany?.attendance.startDate ? dayjs(dataCompany?.attendance.startDate).format('DD/MM/YYYY') : '-'}
                      {' - '}
                      { dataCompany?.attendance.endDate ? dayjs(dataCompany?.attendance.endDate).format('DD/MM/YYYY') : '-'}
                    </span>
                  </Button>
                </div>
              </div>
            </div>
            <Divider />
            <AttendanceEmployeeInformation dataEmployee={dataEmployee || {}} />
            <Divider />

            <div className='p-5'>
              <div className='flex items-center gap-2 justify-between border-b border-[#2f2b3d1f] pb-2'>
                <div className='text-[18px] font-medium text-[#2f2b3d]'>
                  {__('Payroll')} {monthName || '-'}
                </div>
              </div>
              <div className='grid grid-cols-3 gap-5 mt-4'>
                <div>
                  <FormInput
                    name='gross_pay'
                    label={__('Gross Pay')}
                    placeholder={__('Enter Gross Pay')}
                    control={methods.control}
                    className='w-full'
                    onClear={() => {
                      methods.setValue('gross_pay', undefined)
                    }}
                  />
                </div>
                <div>
                  <FormInput
                    name='net_pay'
                    label={__('Net Pay')}
                    placeholder={__('Enter Net Pay')}
                    control={methods.control}
                    className='w-full'
                    onClear={() => {
                      methods.setValue('net_pay', undefined)
                    }}
                  />
                </div>
                <div>
                  <label htmlFor="daily_wage">{__('Daily Wage') } <span style={{ color: 'red' }}>*</span></label>
                  <FormInput
                    name='daily_wage'
                    placeholder={__('Enter Daily Wage')}
                    control={methods.control}
                    className='w-full'
                    onClear={() => {
                      methods.setValue('daily_wage', undefined)
                    }}
                  />
                </div>
              </div>
            </div>

            <Divider />

            <AttendanceEmployeeTotal dataEmployee={dataEmployee || {}} />

            <AttendanceEmployeeTable data={dataEmployee?.data?.data || []} minimum_working_days={dataCompany?.minimum_working_days} />
          </Card>
          {isToggle && (
            <Card sx={{ borderRadius: 3, width: '266px' }}>
              <div className='p-4'>
                <Button
                  variant='tonal'
                  type='button'
                  size='small'
                  className='block w-full mb-3'
                  onClick={() => {
                    methods.reset()
                  }}
                >
                  <span className='text-[15px] font-medium'>{__('Reset')}</span>
                </Button>
                <Button
                  type='submit'
                  variant='tonal'
                  size='small'
                  className='block w-full bg-[#28c76f29]'
                  disabled={isUpdateAttendancePayrollPending}
                >
                  <span className='text-[15px] font-medium text-[#28c76f]'>{__('Save')}</span>
                </Button>
              </div>
            </Card>
          )}
        </div>
      </form>
    </FormProvider>
  )
}
